"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { HotkeysProvider as TanStackHotkeysProvider, useHotkeys } from "@tanstack/react-hotkeys";
import type { Hotkey } from "@tanstack/react-hotkeys";
import { DEFAULT_BINDINGS, HOTKEY_SEQUENCES, loadCustomBindings, saveCustomBindings, type ReaderKind } from "@/lib/hotkeys";

// ── Types ──

type Bindings = Record<string, string[]>;
type BindingSetter = (sequenceId: string, slotIndex: number, hotkey: string | null) => void;
interface HotkeyBindingsContextType {
  bindings: Bindings;
  setBinding: BindingSetter;
  resetBinding: (sequenceId: string, slotIndex: number) => void;
  resetAll: () => void;
}
export interface NavigationCallbacks {
  previousChapter?: () => void;
  nextChapter?: () => void;
  previousPage?: () => void;
  nextPage?: () => void;
  skipBack?: () => void;
  skipForward?: () => void;
}

// ── Context ──

const HotkeyBindingsContext = createContext<HotkeyBindingsContextType | null>(null);
export function useCustomBindings() {
  const ctx = useContext(HotkeyBindingsContext);
  if (!ctx) {
    throw new Error("useCustomBindings must be used within AnimaHotkeysProvider");
  }
  return ctx;
}

// ── Provider ──

export function AnimaHotkeysProvider(t0) {
  const $ = _c(11);
  const {
    children
  } = t0;
  const [bindings, setBindings] = useState(loadCustomBindings);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (sequenceId, slotIndex, hotkey) => {
      setBindings(prev => {
        const defaults = DEFAULT_BINDINGS[sequenceId] ?? [];
        const current = [...(prev[sequenceId] ?? defaults)];
        if (hotkey === null) {
          current[slotIndex] = "";
        } else {
          current[slotIndex] = hotkey;
        }
        const next = {
          ...prev,
          [sequenceId]: current.filter(_temp)
        };
        saveCustomBindings(next);
        return next;
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setBinding = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (sequenceId_0, slotIndex_0) => {
      setBindings(prev_0 => {
        const defaults_0 = DEFAULT_BINDINGS[sequenceId_0] ?? [];
        const current_0 = [...(prev_0[sequenceId_0] ?? defaults_0)];
        current_0[slotIndex_0] = defaults_0[slotIndex_0] ?? "";
        const next_0 = {
          ...prev_0,
          [sequenceId_0]: current_0.filter(_temp2)
        };
        saveCustomBindings(next_0);
        return next_0;
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const resetBinding = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const fresh = {
        ...DEFAULT_BINDINGS
      };
      setBindings(fresh);
      saveCustomBindings(fresh);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const resetAll = t3;
  let t4;
  if ($[3] !== bindings) {
    t4 = {
      bindings,
      setBinding,
      resetBinding,
      resetAll
    };
    $[3] = bindings;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const value = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      hotkey: {
        preventDefault: true
      }
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== children) {
    t6 = <TanStackHotkeysProvider defaultOptions={t5}>{children}</TanStackHotkeysProvider>;
    $[6] = children;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6 || $[9] !== value) {
    t7 = <HotkeyBindingsContext.Provider value={value}>{t6}</HotkeyBindingsContext.Provider>;
    $[8] = t6;
    $[9] = value;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}

// ── useNavigationHotkeys ──

/**
 * Registers all navigation hotkeys for a given reader kind.
 * Call this in reader components. Provide stable callbacks (useCallback).
 */
function _temp2(b_0) {
  return b_0 !== "";
}
function _temp(b) {
  return b !== "";
}
export function useNavigationHotkeys(kind, enabled, callbacks) {
  const $ = _c(7);
  const {
    bindings
  } = useCustomBindings();
  let t0;
  if ($[0] !== bindings) {
    t0 = id => {
      const custom = bindings[id] ?? DEFAULT_BINDINGS[id] ?? [];
      return custom.filter(_temp3);
    };
    $[0] = bindings;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getBindings = t0;
  let defs;
  if ($[2] !== callbacks || $[3] !== enabled || $[4] !== getBindings || $[5] !== kind) {
    defs = [];
    const add = (ids, cb) => {
      if (!cb) {
        return;
      }
      for (const id_0 of ids) {
        for (const hk of getBindings(id_0)) {
          defs.push({
            hotkey: hk,
            callback: cb,
            options: {
              enabled
            }
          });
        }
      }
    };
    if (kind === "novel") {
      add(["novel:chapterPrev"], callbacks.previousChapter);
      add(["novel:chapterNext"], callbacks.nextChapter);
      add(["novel:scrollUp"], callbacks.previousPage);
      add(["novel:scrollDown"], callbacks.nextPage);
    } else {
      if (kind === "manga") {
        add(["manga:chapterPrev"], callbacks.previousChapter);
        add(["manga:chapterNext"], callbacks.nextChapter);
        add(["manga:pagePrev"], callbacks.previousPage);
        add(["manga:pageNext"], callbacks.nextPage);
      } else {
        if (kind === "anime") {
          add(["anime:episodePrev"], callbacks.previousChapter);
          add(["anime:episodeNext"], callbacks.nextChapter);
          add(["anime:skipBack5s"], callbacks.skipBack);
          add(["anime:skipForward5s"], callbacks.skipForward);
        }
      }
    }
    $[2] = callbacks;
    $[3] = enabled;
    $[4] = getBindings;
    $[5] = kind;
    $[6] = defs;
  } else {
    defs = $[6];
  }
  const definitions = defs;
  useHotkeys(definitions);
}
function _temp3(b) {
  return b.length > 0;
}